import { useEffect, useRef, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../ui/dialog";
import { Images, Loader2, Check, AlertCircle, Sparkles } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { PRODUCT_TYPES } from "@/lib/productTypes";

async function compressFile(file, { maxDim = 1600, quality = 0.82, maxOutputBytes = 3_500_000 } = {}) {
    const readRaw = () => new Promise((resolve, reject) => {
        const r = new FileReader();
        r.onload = () => resolve(r.result);
        r.onerror = reject;
        r.readAsDataURL(file);
    });
    try {
        const raw = await readRaw();
        const img = await new Promise((resolve, reject) => {
            const i = new Image();
            i.onload = () => resolve(i);
            i.onerror = reject;
            i.src = raw;
        });
        const scale = Math.min(1, maxDim / Math.max(img.width, img.height));
        const w = Math.round(img.width * scale);
        const h = Math.round(img.height * scale);
        const canvas = document.createElement("canvas");
        canvas.width = w; canvas.height = h;
        const ctx = canvas.getContext("2d");
        ctx.drawImage(img, 0, 0, w, h);
        let q = quality;
        let out = canvas.toDataURL("image/jpeg", q);
        while (out.length > maxOutputBytes && q > 0.4) { q -= 0.1; out = canvas.toDataURL("image/jpeg", q); }
        return out;
    } catch {
        return await readRaw();
    }
}

const CONCURRENCY = 4; // parallel uploads

export default function BulkPhotoDialog({ open, onOpenChange, onDone, defaultProductType = "saree", categories = [] }) {
    const [files, setFiles] = useState([]);
    const [running, setRunning] = useState(false);
    const [items, setItems] = useState([]); // [{name, status: 'pending'|'uploading'|'generating'|'done'|'failed'}]
    const [productType, setProductType] = useState(defaultProductType);
    const [category, setCategory] = useState("");
    const [dragOver, setDragOver] = useState(false);
    const inputRef = useRef(null);

    useEffect(() => {
        if (!open) { setFiles([]); setItems([]); setRunning(false); setCategory(""); setDragOver(false); }
        else { setProductType(defaultProductType); }
    }, [open, defaultProductType]);

    const acceptFiles = (fileList) => {
        const arr = Array.from(fileList || []).filter((f) => f.type.startsWith("image/")).slice(0, 100);
        if (!arr.length) {
            toast.error("Only image files are accepted");
            return;
        }
        setFiles(arr);
    };

    const onPick = (e) => acceptFiles(e.target.files);

    const onDrop = (e) => {
        e.preventDefault();
        setDragOver(false);
        acceptFiles(e.dataTransfer?.files);
    };

    const updateItem = (idx, patch) => {
        setItems((arr) => arr.map((it, i) => (i === idx ? { ...it, ...patch } : it)));
    };

    const processOne = async (file, idx) => {
        try {
            updateItem(idx, { status: "uploading" });
            const dataUrl = await compressFile(file);
            const { data: saree } = await api.post("/admin/sarees", {
                price: 0,
                images: [dataUrl],
                is_new_arrival: true,
                product_type: productType,
                category: category || null,
            });
            updateItem(idx, { status: "generating", sareeId: saree.id });
            const { data: ai } = await api.post(`/admin/sarees/${saree.id}/magic-catalog`, { style: "modern", skin_tone: "medium" });
            const jobId = ai.jobs?.[0]?.job_id;
            if (!jobId) {
                updateItem(idx, { status: "done" });
                return;
            }
            const start = Date.now();
            while (Date.now() - start < 360000) {
                await new Promise((r) => setTimeout(r, 4000));
                try {
                    const { data: job } = await api.get(`/admin/ai-jobs/${jobId}`);
                    if (job.status === "done") { updateItem(idx, { status: "done", provider: job.provider }); return; }
                    if (job.status === "failed") { updateItem(idx, { status: "failed", error: job.error }); return; }
                } catch { /* keep polling */ }
            }
            updateItem(idx, { status: "failed", error: "Timed out" });
        } catch (e) {
            updateItem(idx, { status: "failed", error: formatApiErrorDetail(e.response?.data?.detail) || "Failed" });
        }
    };

    const start = async () => {
        if (!files.length) return;
        setItems(files.map((f) => ({ name: f.name, status: "pending" })));
        setRunning(true);
        let cursor = 0;
        const workers = Array.from({ length: Math.min(CONCURRENCY, files.length) }).map(async () => {
            while (cursor < files.length) {
                const my = cursor++;
                await processOne(files[my], my);
            }
        });
        await Promise.all(workers);
        setRunning(false);
        const ok = items.filter((i) => i.status === "done").length;
        toast.success(`Done — ${ok} item${ok === 1 ? "" : "s"} added with AI photos`);
        onDone?.();
    };

    const counts = items.reduce((acc, it) => {
        acc[it.status] = (acc[it.status] || 0) + 1;
        return acc;
    }, {});
    const total = items.length;
    const completed = (counts.done || 0) + (counts.failed || 0);

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="bg-brand-paper border-brand-border max-w-lg max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                    <DialogTitle className="font-serif text-3xl flex items-center gap-2">
                        <Images className="w-6 h-6 text-brand-gold" strokeWidth={1.4} />
                        Bulk photo upload
                    </DialogTitle>
                </DialogHeader>
                <p className="text-brand-muted text-sm mt-1">
                    Pick up to 100 photos at once. Each photo becomes a separate item with its own AI model image. Add prices later from the catalog tab.
                </p>

                {!items.length ? (
                    <div className="mt-6 space-y-4">
                        <div className="grid grid-cols-2 gap-3">
                            <div>
                                <label className="ayraa-overline">What are these photos of?</label>
                                <select
                                    className="ayraa-input mt-1.5"
                                    value={productType}
                                    onChange={(e) => setProductType(e.target.value)}
                                    data-testid="bulk-product-type-select"
                                >
                                    {PRODUCT_TYPES.map((p) => <option key={p.value} value={p.value}>{p.label}</option>)}
                                </select>
                            </div>
                            <div>
                                <label className="ayraa-overline">Catalog row <span className="text-brand-soft text-[10px] tracking-normal">(optional)</span></label>
                                <select
                                    className="ayraa-input mt-1.5"
                                    value={category}
                                    onChange={(e) => setCategory(e.target.value)}
                                    data-testid="bulk-category-select"
                                >
                                    <option value="">— None —</option>
                                    {categories.map((c) => <option key={c.id} value={c.slug}>{c.name}</option>)}
                                </select>
                            </div>
                        </div>
                        <label
                            className={`block aspect-video rounded-md border border-dashed grid place-items-center cursor-pointer transition ${
                                dragOver
                                    ? "border-brand-primary bg-brand-primary/5"
                                    : "border-brand-border bg-brand-subtle/40 hover:bg-brand-subtle"
                            }`}
                            data-testid="bulk-dropzone"
                            onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
                            onDragEnter={(e) => { e.preventDefault(); setDragOver(true); }}
                            onDragLeave={() => setDragOver(false)}
                            onDrop={onDrop}
                        >
                            <input ref={inputRef} type="file" accept="image/*" multiple className="hidden" onChange={onPick} data-testid="bulk-files-input" />
                            <div className="text-center text-brand-muted px-4">
                                <Images className="w-7 h-7 mx-auto" strokeWidth={1.4} />
                                <div className="text-sm mt-2">{dragOver ? "Drop to add" : "Tap or drag & drop 1–100 photos"}</div>
                                <div className="text-xs text-brand-soft mt-0.5">JPG / PNG · auto-compressed on your phone</div>
                                {files.length ? <div className="text-xs text-brand-primary mt-1">{files.length} photo{files.length === 1 ? "" : "s"} ready</div> : null}
                            </div>
                        </label>
                        <div className="text-xs text-brand-muted leading-relaxed">
                            <strong>Tip:</strong> one item per photo. Plain wall behind. Daylight or single tube light. We&apos;ll handle the rest.
                        </div>
                    </div>
                ) : (
                    <div className="mt-6 space-y-3" data-testid="bulk-progress">
                        <div className="flex items-center justify-between text-sm">
                            <span className="ayraa-overline">Progress</span>
                            <span>{completed} / {total} done</span>
                        </div>
                        <div className="h-1.5 bg-brand-subtle rounded-full overflow-hidden">
                            <div className="h-full bg-brand-primary transition-all" style={{ width: total ? `${(completed / total) * 100}%` : 0 }} />
                        </div>
                        <div className="grid grid-cols-3 gap-2">
                            <Stat label="Generating" value={(counts.uploading || 0) + (counts.generating || 0)} />
                            <Stat label="Done" value={counts.done || 0} />
                            <Stat label="Failed" value={counts.failed || 0} />
                        </div>
                        <div className="max-h-48 overflow-y-auto space-y-1 mt-3">
                            {items.map((it, i) => (
                                <div key={i} className="flex items-center gap-2 text-xs px-2 py-1.5 rounded bg-brand-subtle/40">
                                    {it.status === "done" ? <Check className="w-3.5 h-3.5 text-brand-primary" strokeWidth={2} /> :
                                        it.status === "failed" ? <AlertCircle className="w-3.5 h-3.5 text-brand-primary" strokeWidth={2} /> :
                                            it.status === "pending" ? <span className="w-3.5 h-3.5 rounded-full border border-brand-soft inline-block" /> :
                                                <Loader2 className="w-3.5 h-3.5 text-brand-primary animate-spin" strokeWidth={2} />}
                                    <span className="flex-1 truncate">{it.name}</span>
                                    <span className="text-brand-muted">
                                        {it.status === "done" && it.provider === "cache" ? "cached · ₹0" : it.status}
                                    </span>
                                </div>
                            ))}
                        </div>
                    </div>
                )}

                <div className="mt-6 flex items-center justify-end gap-3">
                    <div className="flex gap-2">
                        <button className="ayraa-btn-outline" onClick={() => onOpenChange(false)} disabled={running}>Close</button>
                        {!items.length ? (
                            <button className="ayraa-btn-primary" disabled>Pick photos first</button>
                        ) : !running && completed < total ? (
                            <button className="ayraa-btn-primary" onClick={start} data-testid="bulk-start-btn">
                                <Sparkles className="w-4 h-4 mr-1.5" strokeWidth={1.5} /> Build {total} item{total === 1 ? "" : "s"}
                            </button>
                        ) : completed >= total ? (
                            <button className="ayraa-btn-primary" onClick={() => onOpenChange(false)} data-testid="bulk-done-btn">Done</button>
                        ) : (
                            <button className="ayraa-btn-primary" disabled>Working… {completed}/{total}</button>
                        )}
                    </div>
                </div>
            </DialogContent>
        </Dialog>
    );
}

function Stat({ label, value }) {
    return (
        <div className="ayraa-card p-2 text-center">
            <div className="ayraa-overline">{label}</div>
            <div className="font-serif text-xl mt-0.5">{value}</div>
        </div>
    );
}
